import { localDateTime, localTimeCandidates } from "./consolidationPresentation.js";
import { visitFullyExcluded } from "./consolidationExecution.js";
import { loadingGroup } from "./consolidationLoadingGroups.js";
import { sheetBreaks } from "./consolidationBreaks.js";

const MATCH_WINDOW_MIN = 20;

export function minutesOf(value, row) {
  const local = localDateTime(value, row.timezone);
  if (!local) return null;
  const days = Math.round((Date.parse(`${local.slice(0, 10)}T00:00:00Z`) - Date.parse(`${row.dispatch_date}T00:00:00Z`)) / 86400000);
  return days * 1440 + Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
}

export function instantOf(minutes, row) {
  if (minutes == null) return null;
  const local = new Date(Date.parse(`${row.dispatch_date}T00:00:00Z`) + Math.round(minutes) * 60000).toISOString().slice(0, 16);
  const candidates = localTimeCandidates(local, row.timezone);
  return candidates.length ? candidates[0].instant : null;
}

// Arrêt Geotab de chaque arrêt du relevé, Chargé PM compris.
// 1. Par numéro de commande : un même arrêt réel peut couvrir plusieurs arrêts du relevé (fermes voisines, Aspiration).
//    Le chargement du matin des commandes préchargées va à la ligne passée à « Chargé ce jour ».
// 2. Sinon, pour un arrêt dont aucune commande n'est connue de Geotab : même type et heure prévue proche.
export function matchGeotabSites(row, truck) {
  const loads = (truck?.comparison?.visits || []).filter((v) => v.kind === "loading")
    .map((v) => ({ idx: `L${v.arr}`, kind: "loading", order_ids: v.order_ids || [], real_arrival: v.arr, real_departure: v.dep }));
  const all = [...(truck?.comparison?.sites || []).filter((s) => s.kind !== "depot"), ...loads];
  const found = (s) => s?.real_arrival != null;
  const used = new Set();
  const matches = new Map();
  const pending = [];
  row.visits.forEach((visit, index) => {
    if (visit.status === "inherited") return;
    const labels = new Set((visit.orders || []).map((o) => o.label));
    const holds = (s) => (s.order_ids || []).some((id) => labels.has(id));
    if (!all.some(holds)) {
      pending.push([visit, index]);
      return;
    }
    // Même type d'abord (une commande est chargée à la meunerie puis livrée à la ferme), sinon le lieu réuni qui la contient.
    const sameKind = all.find((s) => s.kind === visit.kind && holds(s));
    const site = sameKind ? (found(sameKind) ? sameKind : null) : all.find((s) => holds(s) && found(s));
    if (site) {
      used.add(site.idx);
      matches.set(index, site);
    }
  });
  pending.forEach(([visit, index]) => {
    const planned = minutesOf(visit.planned_arrival, row);
    const site = planned == null ? null : all
      .filter((s) => found(s) && !used.has(s.idx) && s.kind === visit.kind && Math.abs(s.plan_arrival - planned) <= MATCH_WINDOW_MIN)
      .sort((a, b) => Math.abs(a.plan_arrival - planned) - Math.abs(b.plan_arrival - planned))[0];
    if (site) {
      used.add(site.idx);
      matches.set(index, site);
    }
  });
  return matches;
}

// Début et fin du service : carnet de route du chauffeur (inspection et fin de journée comprises), sinon départ
// du camion moins l'inspection prévue, et retour au dépôt. Le plus tôt et le plus tard des deux couvrent un chauffeur
// qui a roulé sans être connecté.
export function geotabServicePeriod(truck) {
  const duty = truck?.duty || {};
  const cmp = truck?.comparison || {};
  const pick = (logbook, trip, keepLogbook) => (logbook == null ? [trip, "trajet"]
    : trip == null || keepLogbook(logbook, trip) ? [logbook, "carnet"] : [trip, "trajet"]);
  const start = pick(duty.start, cmp.real_departure, (a, b) => a <= b);
  const plan = truck?.plan;
  const inspection = plan?.start != null && plan?.departure != null ? Math.max(0, plan.departure - plan.start) : 0;
  return {
    service_start: start[1] === "trajet" && start[0] != null ? [start[0] - inspection, "trajet"] : start,
    service_end: pick(duty.end, cmp.real_return, (a, b) => a >= b),
  };
}

// Après le retour au dépôt, jusqu'à la fin du carnet de route : le chauffeur a repris la route (Chargé PM, livraisons
// du lendemain), ou son carnet est resté ouvert, camion immobile au dépôt. null si la fin du service est le retour.
export const IDLE_LOGBOOK_MIN = 60;
const plain = (text) => (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
export function afterReturn(row, truck) {
  const cmp = truck?.comparison;
  const back = cmp?.real_return;
  const [end, source] = geotabServicePeriod(truck).service_end;
  if (back == null || source !== "carnet" || !(end > back)) return null;
  const visits = cmp.visits.filter((v) => v.arr >= back && v.arr < end);
  const later = visits.filter((v) => v.arr > back);
  if (!later.some((v) => v.kind !== "depot")) return end - back >= IDLE_LOGBOOK_MIN ? { idle: true, back, end } : null;
  // Arrêt pendant une pause « Hors service » du carnet ; arrêt du relevé au même nom (Chargé PM fait après le retour).
  const paused = (v) => (truck.breaks || []).some(([a, b]) => Math.min(b, v.dep) - Math.max(a, v.arr) >= (v.dep - v.arr) / 2);
  const stops = visits.map((v) => {
    const stop = { ...v, dep: Math.min(v.dep, end) };
    if (v.kind === "depot") return { ...stop, type: "depot" };
    if (v.dwell < 10) return { ...stop, type: "brief" };
    if (paused(v)) return { ...stop, type: "pause" };
    const name = v.place?.source === "address" ? v.place.name.split(", ").slice(1).join(", ") || v.place.name : v.place?.name;
    const visit = name ? row.visits.find((candidate) => plain(candidate.label) === plain(name)) : null;
    return { ...stop, type: "stop", name: visit?.label || name || "Arrêt", kind: visit?.kind || null, chargedPm: visit?.pm?.role === "source" };
  });
  return {
    idle: false, back, end, stops,
    km: later.every((v) => v.km != null) ? Math.round(later.reduce((sum, v) => sum + v.km, 0)) : null,
    places: stops.filter((s) => s.type === "stop").map((s) => s.name).filter((name, i, all) => name !== all[i - 1]),
  };
}

// Passage au dépôt entre le départ et le retour du camion (le « D » du Gantt), 10 min ou plus : une pause proposée, à classer
// payée (réparation au garage) ou non. L'arrêt au retour compte aussi quand le camion repart ensuite (Chargé PM, chez le
// chauffeur) : il est dans le service. Une plage qui touche le dîner ou une pause déjà saisie n'est pas reprise.
export const DEPOT_BREAK_MIN = 10;
export function depotBreaks(row, truck) {
  const cmp = truck?.comparison;
  if (row.manual_order_id || cmp?.real_departure == null) return [];
  const after = afterReturn(row, truck);
  const until = after && !after.idle ? after.end : cmp.real_return;
  const taken = [...(row.no_lunch ? [] : [[row.lunch_start, row.lunch_end]]), ...sheetBreaks(row).map(pause => [pause.start, pause.end])]
    .filter(([from, to]) => from && to).map(([from, to]) => [minutesOf(from, row), minutesOf(to, row)]);
  return (cmp.visits || []).filter(v => v.kind === "depot" && !v.after_day && v.arr > cmp.real_departure
      && (until == null || v.arr < until) && v.dep - v.arr >= DEPOT_BREAK_MIN
      && !taken.some(([from, to]) => Math.min(to, v.dep) > Math.max(from, v.arr)))
    .map(v => ({ start: instantOf(v.arr, row), end: instantOf(v.dep, row), paid: null }));
}

export const geotabTruckOf = (data, row) => data?.trucks?.find((t) => t.truck_id === row.truck_label || t.truck_name === row.truck_number) || null;

// Pour la liste des relevés : « + 28-04 » (journée partagée) ou « (fait avec 13-02) » (journée entière avec un autre camion).
export function otherTrucksLabel(truck) {
  const used = truck?.trucks_used || [];
  const others = used.filter((u) => !u.planned).map((u) => u.name);
  if (!others.length) return "";
  return used.some((u) => u.planned) ? `+ ${others.join(" + ")}` : `(fait avec ${others.join(" + ")})`;
}

// Sur un dispatch partagé entre plusieurs chauffeurs, indiquer le camion au premier arrêt fait avec chacun
// dans l'ordre réel, quand le chauffeur n'a pas seulement conduit celui du Dispatch.
export function truckStartByVisit(row, truck) {
  const used = truck?.trucks_used || [];
  const byVisit = new Map();
  if ((truck?.dispatch_driver_count ?? 0) < 2 || !used.some((u) => !u.planned)) return byVisit;
  const names = new Map(used.map((u) => [u.device_id, u.name]));
  const firsts = new Map();
  matchGeotabSites(row, truck).forEach((site, index) => {
    if (!names.has(site.real_device_id)) return;
    const rank = [site.real_arrival, row.visits[index].actual_sequence ?? index];
    const best = firsts.get(site.real_device_id);
    if (!best || rank[0] < best.rank[0] || (rank[0] === best.rank[0] && rank[1] < best.rank[1])) firsts.set(site.real_device_id, { rank, index });
  });
  firsts.forEach(({ index }, device) => byVisit.set(row.visits[index].id, names.get(device)));
  return byVisit;
}

// Le chauffeur a conduit un autre camion que celui du Dispatch, ou plusieurs.
export function trucksNote(truck) {
  const used = truck?.trucks_used || [];
  if (!used.some((u) => !u.planned)) return "";
  return used.length > 1 ? `${used.length} camions : ${used.map((u) => u.name).join(" puis ")}` : `camion ${used[0].name} au lieu du ${truck.truck_name}`;
}

// Plusieurs arrêts du relevé au même arrêt réel (fermes voisines, Aspiration) : le temps sur place est partagé dans
// l'ordre du relevé, au prorata de leur durée prévue (à parts égales sans durée prévue). Chacun commence au départ du
// précédent : la chronologie reste valide et les heures se répartissent entre les coops.
export function geotabVisitTimes(row, matches) {
  const groups = new Map();
  matches.forEach((site, index) => groups.set(site, [...(groups.get(site) || []), index]));
  const times = new Map();
  groups.forEach((indexes, site) => {
    if (site.real_departure == null) return;
    // Préchargement compté une ligne par commande : un seul passage. La première ligne porte l'arrivée, les autres
    // partent à la même heure sans nouvelle arrivée (sinon elles « arriveraient » avant le départ de la précédente).
    if (indexes.every((index) => loadingGroup(row.visits, row.visits[index]).length > 1)) {
      const first = Math.min(...indexes.map((index) => row.visits[index].actual_sequence ?? index));
      indexes.forEach((index) => times.set(index, [(row.visits[index].actual_sequence ?? index) === first ? site.real_arrival : null, site.real_departure]));
      return;
    }
    const ordered = indexes.slice().sort((a, b) => (row.visits[a].actual_sequence ?? a) - (row.visits[b].actual_sequence ?? b));
    const weights = ordered.map((index) => {
      const visit = row.visits[index];
      return Math.max(0, (minutesOf(visit.planned_departure, row) ?? 0) - (minutesOf(visit.planned_arrival, row) ?? 0));
    });
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    let cursor = site.real_arrival;
    ordered.forEach((index, position) => {
      const share = total > 0 ? weights[position] / total : 1 / ordered.length;
      const end = position === ordered.length - 1 ? site.real_departure : cursor + (site.real_departure - site.real_arrival) * share;
      times.set(index, [cursor, end]);
      cursor = end;
    });
  });
  return times;
}

const withGeotabTimes = (visit, [arrival, departure], row) => ({
  ...visit,
  arrival: instantOf(arrival, row),
  departure: instantOf(departure, row),
  status: visit.status === "unset" ? "performed" : visit.status,
  geotab: true,
});

// Seules les valeurs locales du formulaire changent ; la sauvegarde reste une action explicite.
// Chaque heure va à l'arrêt du même lieu, quelle que soit sa ligne : un chauffeur qui a inversé deux arrêts
// garde les bonnes heures sur les bons clients, puis le relevé reprend l'ordre réel.
export function fillFromGeotab(row, truck, { preserveExclusions = false } = {}) {
  const matches = matchGeotabSites(row, truck);
  const times = geotabVisitTimes(row, matches);
  const excluded = visit => preserveExclusions && visitFullyExcluded(row, visit);
  row.visits.forEach((visit, index) => { if (excluded(visit)) times.delete(index); });
  const eligible = row.visits.filter((v) => v.status !== "inherited" && !excluded(v)).length;
  let replaced = 0;
  const visits = row.visits.map((visit, index) => {
    if (!times.has(index)) return visit;
    if (visit.arrival || visit.departure) replaced += 1;
    return withGeotabTimes(visit, times.get(index), row);
  });
  const service = row.manual_order_id ? [] : Object.entries(geotabServicePeriod(truck)).filter(([, [minutes]]) => minutes != null);
  const breaks = depotBreaks(row, truck);
  // Départ réel du camion : l'attente avant ce départ compte dans l'inspection.
  const departure = row.manual_order_id || truck?.comparison?.real_departure == null ? null : instantOf(truck.comparison.real_departure, row);
  const next = {
    ...row,
    visits,
    ...(breaks.length ? { breaks: [...sheetBreaks(row), ...breaks] } : {}),
    ...Object.fromEntries(service.map(([key, [minutes]]) => [key, instantOf(minutes, row)])),
    geotab_service: Object.fromEntries(service.map(([key, [, source]]) => [key, source])),
    ...(departure ? { truck_departure: departure } : {}),
  };
  return {
    row: next, filled: times.size, replaced, missing: eligible - times.size, breaks,
    service: service.length, serviceReplaced: service.filter(([key]) => row[key]).length, trucks: trucksNote(truck),
    // Un départ du camion déjà saisi, à une autre heure, est remplacé : la fenêtre le dit.
    departureReplaced: Boolean(departure && row.truck_departure && Date.parse(row.truck_departure) !== Date.parse(departure)),
    afterReturn: row.manual_order_id ? null : afterReturn(row, truck),
  };
}
